<template>
  <div class="container" :class="{ 'container--small': !props.fromTreeSection }">
    <a-typography-title v-if="!props.fromTreeSection" :level="3" class="container__title">
      Configura el tiempo de espera
    </a-typography-title>
    <p class="container__description">
      Establece la acción a realizar cuando se supere el tiempo máximo de espera de una respuesta
      del contacto.
    </p>
    <custom-scrollbar :height="heightSection">
      <div class="container__radios">
        <a-radio-group v-model:value="waitTimeActionValue" :disabled="props.disabled">
          <a-radio
            :style="radioStyle"
            :value="WaitTimeAction.None"
            class="container__radio"
            :disabled="props.disabled"
          >
            <div class="padding-left-20">
              <a-typography-text strong>Ninguna acción automática</a-typography-text>
              <p class="mrg-bottom-0 radio__description">
                El chatbot esperará la respuesta del contacto y no se resolverá el ticket, a menos
                que un usuario lo haga manualmente.
              </p>
            </div>
          </a-radio>
          <a-radio
            :style="radioStyle"
            :value="WaitTimeAction.Resolve"
            class="container__radio"
            :disabled="props.disabled"
          >
            <div class="padding-left-20">
              <a-typography-text strong>Resolver ticket</a-typography-text>
              <p class="radio__description">
                Se establece un tiempo máximo de espera de la respuesta del contacto para proceder a
                resolver el ticket asignado.
              </p>
              <a-form
                :model="formStateActionResolve"
                ref="formRef"
                layout="vertical"
                :rules="rules"
              >
                <a-form-item label="Tiempo para reinicio de chatbot" name="timer">
                  <a-input-number
                    v-model:value="formStateActionResolve.timer"
                    :disabled="props.disabled || disabledResolveAction"
                    class="form__timer"
                  />
                  <span class="mrg-left-12"> Minutos </span>
                </a-form-item>
                <a-form-item label="Mensaje" name="timer_message">
                  <a-textarea
                    v-model:value="formStateActionResolve.timer_message"
                    :disabled="props.disabled || disabledResolveAction"
                    placeholder="Escribe aquí..."
                    :maxlength="100"
                    :autosize="{ minRows: 2, maxRows: 2 }"
                  />
                </a-form-item>
              </a-form>
            </div>
          </a-radio>
          <a-card class="container__card" :bodyStyle="{ padding: '12px' }">
            <a-radio
              :value="WaitTimeAction.Transfer"
              :disabled="props.disabled"
              style="display: flex"
            >
              <a-typography-text strong style="margin-left: 6px">
                Transferencia a una cola
              </a-typography-text>
            </a-radio>
            <div class="container__card-body">
              <p class="radio__description">
                Se establece un tiempo máximo de espera de la respuesta del contacto para proceder a
                transferir el ticket a una cola.
              </p>
              <a-form
                :model="formStateActionTransfer"
                ref="formRef"
                layout="vertical"
                :rules="rules"
              >
                <a-form-item label="Tiempo para transferir el ticket" name="timer">
                  <a-input-number
                    v-model:value="formStateActionTransfer.timer"
                    :disabled="props.disabled || disabledTransferAction"
                    class="form__timer"
                  />
                  <span class="mrg-left-12"> Minutos </span>
                </a-form-item>
                <div @mousedown.stop @click.stop>
                  <campaign-picker
                    label="Seleccionar campaña"
                    :value="formStateActionTransfer.portfolio"
                    :withOutEntity="true"
                    :showAll="false"
                    :withName="true"
                    :disabled="props.disabled || disabledTransferAction"
                    @update:value="(value: any) => (formStateActionTransfer.portfolio = value)"
                  />
                </div>
                <div @click.stop>
                  <queue-picker
                    label="Seleccionar cola"
                    :value="formStateActionTransfer.queue"
                    :portfolio="formStateActionTransfer.portfolio"
                    :showAll="false"
                    :disabled="props.disabled || disabledTransferAction"
                    @update:value="(value: any) => (formStateActionTransfer.queue = value)"
                  />
                </div>
                <a-form-item label="Mensaje de transferencia" name="timer_message">
                  <a-textarea
                    v-model:value="formStateActionTransfer.timer_message"
                    :disabled="props.disabled || disabledTransferAction"
                    placeholder="Escribe aquí..."
                    :maxlength="100"
                    :autoSize="{ minRows: 2, maxRows: 2 }"
                  />
                </a-form-item>
              </a-form>
            </div>
          </a-card>
        </a-radio-group>
      </div>
    </custom-scrollbar>
  </div>
</template>
<script lang="ts" setup>
import { reactive, onMounted, computed, ref, inject, Ref, watch } from "vue";
import { WaitTimeAction, ChatbotDetails } from "@/@types/chatbots";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const props = defineProps<{
  fromTreeSection?: boolean;
  disabled?: boolean;
}>();

const chatbotDetails = inject("chatbotDetails") as Ref<ChatbotDetails>;
const waitTimeActionValue = ref(chatbotDetails?.value?.wait_time_action || 1);
const formRef = ref();
const formStateActionResolve = reactive({
  timer:
    waitTimeActionValue.value === WaitTimeAction.Resolve ? chatbotDetails?.value?.timer || 1 : 1,
  timer_message:
    waitTimeActionValue.value === WaitTimeAction.Resolve
      ? chatbotDetails?.value?.timer_message || undefined
      : undefined,
});

const formStateActionTransfer = reactive({
  timer:
    waitTimeActionValue.value === WaitTimeAction.Transfer ? chatbotDetails?.value?.timer || 1 : 1,
  timer_message:
    waitTimeActionValue.value === WaitTimeAction.Transfer
      ? chatbotDetails?.value?.timer_message || null
      : null,
  portfolio:
    waitTimeActionValue.value === WaitTimeAction.Transfer
      ? chatbotDetails?.value?.portfolio || undefined
      : undefined,
  queue:
    waitTimeActionValue.value === WaitTimeAction.Transfer
      ? chatbotDetails?.value?.queue || undefined
      : undefined,
});

const radioStyle = reactive({
  display: "flex",
  minHeight: "80px",
  border: "1px solid #D9D9D9",
  padding: "12px",
  borderRadius: "6px",
  width: "100%",
  marginBottom: "12px",
});

const emits = defineEmits<{
  disable: [disabled: boolean];
  activateButton: [activate: boolean];
}>();

onMounted(() => {
  emits("disable", false);
  emits("activateButton", false);
});

const formState = computed(() => {
  let form = {};

  switch (waitTimeActionValue.value) {
    case WaitTimeAction.Resolve:
      form = formStateActionResolve;
      break;
    case WaitTimeAction.Transfer:
      form = formStateActionTransfer;
      break;
  }

  return {
    wait_time_action: waitTimeActionValue,
    ...form,
  };
});

const disabledResolveAction = computed(() => {
  return waitTimeActionValue.value !== WaitTimeAction.Resolve;
});

const disabledTransferAction = computed(() => {
  return waitTimeActionValue.value !== WaitTimeAction.Transfer;
});

const heightSection = computed(() => {
  return props.fromTreeSection ? "calc(100vh - 345px)" : "408px";
});

const rules = computed(() => {
  return {
    timer: [{ required: true, message: "Por favor completa el campo", trigger: "change" }],
    timer_message: [{ required: false, message: "Por favor completa el campo", trigger: "change" }],
    portfolio: [
      {
        required: waitTimeActionValue.value === WaitTimeAction.Transfer ? true : false,
        message: "Por favor completa el campo",
      },
    ],
    queue: [
      {
        required: waitTimeActionValue.value === WaitTimeAction.Transfer ? true : false,
        message: "Por favor completa el campo",
      },
    ],
  };
});

watch(formState, () => emits("activateButton", true), { deep: false });

const handleOk = async (step: number) => {
  let value = step;
  await formRef.value.validate().then(() => {
    value = step + 1;
  });

  return value;
};

defineExpose({ handleOk, formState });
</script>

<style lang="sass" scoped>
.padding-left-20
  padding-left: 20px
.container
  margin: auto
  &--small
    width: 500px
    .container
      &__title, &__description
        text-align: center
  &__radios
    padding-right: 14px
  &__radio
    position: relative
    .radio__description
      color: $text-secondary
.form
  &__timer
    width: 80%
</style>
<style lang="sass" scoped>
.container__radio .ant-radio
  position: absolute
  top: 16px

:global(.container__card .ant-card-body)
  padding: 0 !important
.container__card
  border: 1px solid rgb(217, 217, 217) !important
  margin-bottom: 12px
  &-body
    padding-left: 30px
</style>
